// The Arena's effects: point pops, the combo meter, the level-up flash with
// confetti, and the daily quest chips. Visual only; the numbers come from
// salesGame and arenaPlan.
import React, { useEffect, useRef } from 'react';
import { Flame, Trophy } from 'lucide-react';

export const CSS = `
@keyframes arena-pop { 0% { transform: translateY(8px) scale(.7); opacity: 0 } 15% { transform: translateY(0) scale(1.08); opacity: 1 } 70% { opacity: 1 } 100% { transform: translateY(-46px) scale(1); opacity: 0 } }
.arena-pop { animation: arena-pop 1.6s ease-out forwards; }
@keyframes arena-flash { 0% { opacity: 0 } 10% { opacity: 1 } 80% { opacity: 1 } 100% { opacity: 0 } }
.arena-flash { animation: arena-flash 3.6s ease forwards; }
@keyframes arena-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(255,90,90,.55) } 50% { box-shadow: 0 0 0 10px rgba(255,90,90,0) } }
.arena-pulse { animation: arena-pulse 1.6s ease-out infinite; }
@keyframes arena-ticker { from { transform: translateX(0) } to { transform: translateX(-50%) } }
.arena-ticker { animation: arena-ticker 38s linear infinite; }
.arena-ticker:hover { animation-play-state: paused; }
.arena-glass { background: rgba(10,14,28,.78); backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px); border: 1px solid rgba(255,255,255,.1); }
.arena-scroll::-webkit-scrollbar { width: 6px } .arena-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,.15); border-radius: 3px }
.arena-sheet { background: rgba(9,13,26,.94); backdrop-filter: blur(22px); -webkit-backdrop-filter: blur(22px); border-top: 1px solid rgba(255,255,255,.14); box-shadow: 0 -12px 40px rgba(0,0,0,.45); will-change: transform; }
.arena-nobar { scrollbar-width: none; -ms-overflow-style: none; } .arena-nobar::-webkit-scrollbar { display: none; }
@media (max-width: 767px) {
  /* iOS smears a blurred glass element near the status bar across the whole top of the screen; phones get solid glass instead. */
  .arena-glass { background: rgba(10,14,28,.9); backdrop-filter: none; -webkit-backdrop-filter: none; }
  .arena-sheet { background: rgba(9,13,26,.97); backdrop-filter: none; -webkit-backdrop-filter: none; }
  /* Safari blurs whatever sits under the status bar (its own glass, not ours). A solid strip there gives it nothing to smear. */
  .arena-topfill { background: linear-gradient(to bottom, #070a16 0, #070a16 calc(100% - 24px), rgba(7,10,22,0) 100%); }
  .arena-flag { height: 30px; } .arena-flag .cloth { font-size: 9.5px; padding: 2px 6px 2px 5px; max-width: 128px; overflow: hidden; }
  .arena-flag.current { height: 48px; } .arena-flag.current .cloth { font-size: 12px; max-width: 200px; }
  .maplibregl-ctrl-bottom-right .maplibregl-ctrl-group { display: none; }
  .maplibregl-ctrl-bottom-right, .maplibregl-ctrl-bottom-left { bottom: auto; top: calc(max(10px, env(safe-area-inset-top)) + 50px); }
  .arena-sheet textarea, .arena-sheet input, .arena-sheet select { font-size: 16px; }
}
.maplibregl-ctrl-attrib { font-size: 10px; background: rgba(10,14,28,.6) !important; color: #9aa4c0 } .maplibregl-ctrl-attrib a { color: #9aa4c0 }
.maplibregl-ctrl-group { background: rgba(10,14,28,.78) !important; border: 1px solid rgba(255,255,255,.1) } .maplibregl-ctrl-group button + button { border-top-color: rgba(255,255,255,.1) }
.maplibregl-ctrl-group button .maplibregl-ctrl-icon { filter: invert(1) opacity(.8) }
.arena-flag { position: absolute; display: block; width: 2px; height: 38px; background: transparent; border: 0; padding: 0; cursor: pointer; pointer-events: auto; }
.arena-flag .pole { position: absolute; left: 0; bottom: 0; width: 2px; height: 100%; background: linear-gradient(to top, rgba(255,255,255,.2), rgba(255,255,255,.9)); }
.arena-flag .cloth { position: absolute; left: 2px; top: 0; display: inline-flex; align-items: center; gap: 5px; padding: 3px 8px 3px 7px; font: 700 10.5px/1.1 -apple-system, Inter, system-ui, sans-serif; color: #fff; white-space: nowrap; background: var(--c, #5b8def); border-radius: 0 7px 7px 0; box-shadow: 0 2px 10px rgba(0,0,0,.45); transform-origin: left center; transition: transform .15s ease; }
.arena-flag:hover .cloth { transform: scale(1.08); z-index: 5; }
.arena-flag .score { font-size: 9px; padding: 0 4px; border-radius: 4px; background: rgba(0,0,0,.35); }
.arena-flag .mine { font-size: 8px; opacity: .9; }
.arena-flag.lost { opacity: .4; }
.arena-flag.current { height: 56px; z-index: 10; }
.arena-flag.current .cloth { font-size: 13px; padding: 5px 11px 5px 9px; box-shadow: 0 0 0 2px #fff, 0 0 26px var(--c); animation: arena-wave 1.6s ease-in-out infinite; }
.arena-flag.current .you { font-size: 9px; letter-spacing: .12em; padding: 1px 5px; border-radius: 4px; background: #fff; color: #111; }
@keyframes arena-wave { 0%, 100% { transform: skewY(0deg) } 50% { transform: skewY(-3deg) translateY(-1px) } }
@keyframes arena-hit { 0% { transform: translate(0,0) } 20% { transform: translate(-6px,2px) } 40% { transform: translate(6px,-2px) } 60% { transform: translate(-4px,1px) } 80% { transform: translate(4px,-1px) } 100% { transform: translate(0,0) } }
.arena-hit { animation: arena-hit .45s ease; }
@keyframes arena-rise { from { transform: translateY(14px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
.arena-rise { animation: arena-rise .35s ease-out both; }
`;

export function Pops({ pops }) {
  return (
    <div className="pointer-events-none absolute left-1/2 top-24 -translate-x-1/2 z-20 flex flex-col items-center gap-1">
      {pops.map((x) => (
        <div key={x.id} className="arena-pop flex items-baseline gap-2 px-4 py-2 rounded-full bg-emerald-500 text-white shadow-[0_0_30px_rgba(16,185,129,.6)]">
          <span className="text-2xl font-black leading-none tabular-nums">{x.text}</span>
          {x.big && <span className="text-xs font-bold uppercase tracking-wider opacity-90">{x.big}</span>}
        </div>
      ))}
    </div>
  );
}

export function Combo({ n }) {
  if (n < 2) return null;
  const heat = n >= 10 ? 'from-rose-500 to-amber-400' : n >= 5 ? 'from-orange-500 to-amber-400' : 'from-amber-500 to-yellow-300';
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r ${heat} text-black text-xs font-black shadow-lg ${n >= 5 ? 'arena-pulse' : ''}`} title="Touches in a row with no six-minute gap">
      <Flame className="w-3.5 h-3.5" /> {n}x combo
    </div>
  );
}

export function Quests({ quests }) {
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
      {quests.map((q) => (
        <div key={q.key} title={q.hint} className={`relative flex-shrink-0 overflow-hidden rounded-lg border px-2.5 py-1 text-[11px] font-bold ${q.done ? 'border-emerald-400/60 text-emerald-200 bg-emerald-500/15' : 'border-white/10 text-slate-200 bg-white/5'}`}>
          {!q.done && <span className="absolute inset-y-0 left-0 bg-blue-500/25" style={{ width: `${q.pct}%` }} aria-hidden="true" />}
          <span className="relative">{q.done ? '✓ ' : ''}{q.name}<span className="ml-1.5 tabular-nums opacity-70">{Math.min(q.n, q.target)}/{q.target}</span></span>
        </div>
      ))}
    </div>
  );
}

// A canvas of falling confetti for a few seconds.
export function Confetti({ run }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!run || !ref.current) return undefined;
    const c = ref.current;
    const ctx = c.getContext('2d');
    const W = (c.width = c.offsetWidth * devicePixelRatio);
    const H = (c.height = c.offsetHeight * devicePixelRatio);
    const colors = ['#34d399', '#60a5fa', '#f472b6', '#fbbf24', '#a78bfa', '#f87171', '#ffffff'];
    const parts = Array.from({ length: 220 }, () => ({ x: Math.random() * W, y: -Math.random() * H * 0.4, vx: (Math.random() - 0.5) * 3 * devicePixelRatio, vy: (2 + Math.random() * 4) * devicePixelRatio, r: (3 + Math.random() * 5) * devicePixelRatio, a: Math.random() * Math.PI, va: (Math.random() - 0.5) * 0.3, c: colors[Math.floor(Math.random() * colors.length)] }));
    let frame = 0;
    const t0 = performance.now();
    const step = (now) => {
      ctx.clearRect(0, 0, W, H);
      const fade = Math.max(0, 1 - (now - t0 - 2600) / 900);
      for (const p of parts) {
        p.x += p.vx; p.y += p.vy; p.a += p.va; p.vy += 0.03 * devicePixelRatio;
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.a); ctx.globalAlpha = fade; ctx.fillStyle = p.c; ctx.fillRect(-p.r / 2, -p.r / 4, p.r, p.r / 2); ctx.restore();
      }
      if (now - t0 < 3600) frame = requestAnimationFrame(step); else ctx.clearRect(0, 0, W, H);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [run]);
  return <canvas ref={ref} className="pointer-events-none absolute inset-0 z-30 w-full h-full" aria-hidden="true" />;
}

export function Flash({ cheer }) {
  if (!cheer) return null;
  return (
    <div className="arena-flash pointer-events-none absolute inset-0 z-30 flex items-center justify-center">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-400/20 via-transparent to-transparent" />
      <div className="arena-glass rounded-3xl px-10 py-7 text-center shadow-2xl border-amber-300/40">
        <Trophy className="w-12 h-12 text-amber-300 mx-auto drop-shadow-[0_0_18px_rgba(252,211,77,.8)]" />
        <div className="mt-2 text-3xl font-black text-white tracking-tight">{cheer.title}</div>
        {cheer.text && <div className="mt-1 text-sm text-slate-200">{cheer.text}</div>}
      </div>
    </div>
  );
}

export const Stars = ({ rating }) => {
  const r = Number(rating) || 0;
  return (
    <span className="inline-flex items-center gap-0.5 text-amber-300" aria-label={`${r} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => <span key={i} className={i <= Math.round(r) ? '' : 'opacity-25'}>★</span>)}
    </span>
  );
};
